import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'
import { useMemo } from 'react'
import { keyBy } from 'es-toolkit'
import { PrecursorEmissionReport, useGetPrecursorEmissionReports } from './precursorEmissionReports'
import { EMISSION_REPORTS_QUERY_KEY } from '../emissionReports'

const queryKey = 'precursorMaterials'

export type PrecursorMaterial = {
  id: string
  stockChangeType: 'IN' | 'OUT' | 'ALLOCATED'
  internalId: string | null
  date: string
  quantityInTonnes: number
  proofFilePath: string | null
  details: string | null
  deletedAt: string | null
  precursorEmissionReportId: string
  emissionReportId: string | null
  emissionReport: {
    id: string
    startDate: string
    endDate: string
    productionRoute: {
      id: string
      name: string
    }
  } | null
}

export type PrecursorMaterialWithPrecursorEmissionReport = PrecursorMaterial & {
  precursorEmissionReport: PrecursorEmissionReport
}

export const usePrecursorMaterialsWithPrecursorEmissionReports = (
  organizationId: string,
  installationId: string
): {
  precursorMaterialsWithEmissionReports: PrecursorMaterialWithPrecursorEmissionReport[]
  isLoading: boolean
} => {
  const precursorEmissionReportsQuery = useGetPrecursorEmissionReports(organizationId ?? '')
  const precusorEmiosnReportsByInstallationId = useMemo(
    () =>
      keyBy(
        precursorEmissionReportsQuery.data ?? [],
        precursorEmissionReport => precursorEmissionReport.id
      ),
    [precursorEmissionReportsQuery.data]
  )
  const precursorMaterialsQuery = useGetPrecursorMaterialsForInstallation(installationId)
  const precursorMaterialsNotDeleted = (precursorMaterialsQuery.data ?? []).filter(
    precursorMaterial => !precursorMaterial.deletedAt
  )

  const precursorMaterialsWithEmissionReports = useMemo(
    () =>
      precursorMaterialsNotDeleted.map(precursorMaterial => ({
        ...precursorMaterial,
        precursorEmissionReport:
          precusorEmiosnReportsByInstallationId[precursorMaterial.precursorEmissionReportId]
      })),
    [precursorMaterialsNotDeleted, precusorEmiosnReportsByInstallationId]
  )

  return {
    precursorMaterialsWithEmissionReports,
    isLoading: precursorEmissionReportsQuery.isLoading || precursorMaterialsQuery.isLoading
  }
}

const useGetPrecursorMaterialsForInstallation = (installationId: string) => {
  return useQuery<PrecursorMaterial[]>({
    queryKey: [queryKey, installationId],
    queryFn: async () => {
      const response = await apiClient.get<PrecursorMaterial[]>(
        `/v2/installations/${installationId}/precursor-materials`
      )
      return response.data
    },
    select: data => data.filter(material => !material.deletedAt)
  })
}

export const useCreatePrecursorMaterial = (installationId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {
      stockChangeType: 'IN' | 'OUT' | 'ALLOCATED'
      internalId: string | null
      date: string
      quantityInTonnes: number
      proofFilePath: string | null
      details: string | null
      emissionReportId?: string
      precursorEmissionReportId: string
    }) => {
      const response = await apiClient.post<PrecursorMaterial>(
        `/v2/installations/${installationId}/precursor-materials`,
        payload
      )
      return response.data
    },
    onSuccess: data => {
      queryClient.setQueryData([queryKey, installationId], (old: PrecursorMaterial[]) => [
        ...old,
        data
      ])
      queryClient.refetchQueries({
        queryKey: [EMISSION_REPORTS_QUERY_KEY, data.emissionReportId ?? '']
      })
    }
  })
}

export const useUpdatePrecursorMaterial = (installationId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: {
      precursorMaterialId: string
      updatePayload: {
        stockChangeType: 'IN' | 'OUT' | 'ALLOCATED'
        internalId: string | null
        date: string
        quantityInTonnes: number
        proofFilePath: string | null
        details: string | null
        emissionReportId: string | null
      }
    }) => {
      const response = await apiClient.put<PrecursorMaterial>(
        `/v2/precursor-materials/${payload.precursorMaterialId}`,
        payload.updatePayload
      )
      return response.data
    },
    onSuccess: data => {
      queryClient.setQueryData([queryKey, installationId], (old: PrecursorMaterial[]) =>
        old.map(material => (material.id === data.id ? data : material))
      )
      queryClient.refetchQueries({
        queryKey: [EMISSION_REPORTS_QUERY_KEY, data.emissionReportId ?? '']
      })
    }
  })
}

export const useDeletePrecursorMaterial = (installationId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { precursorMaterialId: string }) => {
      const response = await apiClient.delete<PrecursorMaterial>(
        `/v2/precursor-materials/${payload.precursorMaterialId}`
      )
      return response.data
    },
    onSuccess: data => {
      queryClient.setQueryData([queryKey, installationId], (old: PrecursorMaterial[]) =>
        old.map(material => (material.id === data.id ? data : material))
      )
      queryClient.refetchQueries({
        queryKey: [EMISSION_REPORTS_QUERY_KEY, data.emissionReportId ?? '']
      })
    }
  })
}
